import {
  Component,
  OnInit,
  ViewEncapsulation,
  AfterViewInit,
  OnDestroy,
  ElementRef,
  HostListener,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { LoadingService } from '@shared/loading';
import { EstablecimientoModel } from '@shared/models/establecimiento-model';
import { Chart, registerables } from 'chart.js';
import { endOfMonth, startOfDay, startOfMonth, endOfDay } from 'date-fns';
import { DashboardService } from './services/dashboard.service';
import { COLORS } from './themes/dashboard.colors';
import {
  Dashboard,
  Tarjetas,
  Correlativos,
} from './models/dashboard.interface';
import SwiperCore, { Pagination, SwiperOptions } from 'swiper';
import {
  delay,
  filter,
  finalize,
  map,
  take,
  takeUntil,
  tap,
  distinct,
} from 'rxjs/operators';
import { Observable, Subject, combineLatest } from 'rxjs';
import { ConfigsService, EstablecimientosService } from '@core/yalo';
import { DateConverterService } from '@core/utils/date-converter.service';
import { DaterangeEvent } from '@shared/models/daterange-event';
import { ChartsService } from './services/charts.service';
import { OnboardingService } from '@pages/dashboard/services/onboarding.service';
import { ShepherdService } from 'angular-shepherd';
import Step from 'shepherd.js/src/types/step';
import { PopoverController } from '@ionic/angular';
import { Input } from '@angular/core';
import { Establecimiento } from '../../core/yalo/data-access/yalo-data-access.types';
import { CajasService } from './services/cajasstatus.services';

SwiperCore.use([Pagination]);

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.page.html',
  styleUrls: ['./dashboard.page.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class DashboardPage implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('filterGroup') filterGroup: ElementRef;
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this.isEstablecimientosExpanded && !this.filterGroup.nativeElement.contains(event.target)) {
      this.isEstablecimientosExpanded = false;
    }
  }

  config: SwiperOptions = {
    pagination: { clickable: true },
  };
  

  correlativos: Correlativos[];
  dashboard: Dashboard;
  establecimientos: EstablecimientoModel[];
  tarjetas: Tarjetas;
  isEstablecimientosExpanded = false;

  codEstablecimiento = new FormControl(0);

  fechaInicio: Date = startOfDay(new Date());
  fechaFin: Date = endOfDay(new Date());

  colors = COLORS;
  filterType = 1;

  private _unsubscribeAll = new Subject<void>();

  constructor(
    public chartService: ChartsService,
    private _dashboardService: DashboardService,
    private _dateFormat: DateConverterService,
    private _loading: LoadingService,
    private establecimientosService: EstablecimientosService,
    private onboardingService: OnboardingService,
    private shepherdService: ShepherdService,
    private popoverController: PopoverController,
    public configSvc: ConfigsService
  ) {
    Chart.register(...registerables);
  }

  async ngOnInit() {
    const daterangeComponent = document.querySelector('yalo-daterange');
    this.codEstablecimiento.valueChanges
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((cod) => {
        if (cod) {
          this.establecimientosService.codEstablecimiento = +cod;
          this.obtenerEstadisticas();
        }
      });

    this.onboardingService.showOnboardingDashboard$
      .pipe(
        distinct(),
        takeUntil(this._unsubscribeAll),
        tap((showOnboarding) => {
          if (showOnboarding) {
            document.getElementById('businessProfileButton').click();
            setTimeout(() => {
              this.shepherdService.start();
            }, 200);
          }
        })
      )
      .subscribe();

    combineLatest([
      this.establecimientosService.establecimientos$
    ])
      .pipe(delay(0))
      .subscribe(async ([establecimientos]) => {
        this.establecimientos = establecimientos.map(est => ({...est, selected: true}));
        this.chartService.simboloMoneda = this.configSvc.simboloMoneda;
        const codEstablecimiento =
          await this.establecimientosService.getCodEstablecimiento();
        this.codEstablecimiento.setValue(codEstablecimiento);
      });
  }

  ngAfterViewInit(): void {
    this.shepherdService.defaultStepOptions =
      this.onboardingService.getStepOptionsDefault();
    this.shepherdService.modal = true;
    this.shepherdService.confirmCancel = false;
    this.shepherdService.addSteps(this.onboardingSteps());
    this.applyResponsiveStyles();
    window.addEventListener('resize', () => this.applyResponsiveStyles());
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', () => this.applyResponsiveStyles());
    try {
      this.shepherdService.complete();
    } catch { }
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  private readonly MOBILE_BREAKPOINT = 690;
  private readonly STYLE_DELAY = 100;
  private readonly FONT_SIZE = '1rem';

  private applyResponsiveStyles(): void {
    setTimeout(() => {
      if (window.innerWidth <= this.MOBILE_BREAKPOINT) {
        this.applyDateRangeStyles();
      }
    }, this.STYLE_DELAY);
  }

  private applyDateRangeStyles(): void {
    const daterangeComponent = document.querySelector('yalo-daterange');
    const daterangeInput = daterangeComponent?.shadowRoot?.querySelector('.yc-input');

    if (daterangeInput instanceof HTMLElement) {
      Object.assign(daterangeInput.style, {
        fontSize: this.FONT_SIZE,
        height: '100%',
        alignItems: 'normal'
      });
    }
  }

  onboardingSteps(): Step.StepOptions[] {
    return [
      {
        attachTo: {
          element: 'app-profile',
          // on: 'auto',
        },
        modalOverlayOpeningPadding: 17,
        title: 'Recorrido',
        text: [
          'Si tienes varias empresas, podrás cambiar rápidamente entre ellas en esta sección.',
        ],
        buttons: [
          {
            classes: this.onboardingService.secondaryButtonClasses(),
            text: 'Cancelar',
            action: () => {
              this.shepherdService.cancel();
            },
          },
          {
            classes: this.onboardingService.primaryButtonClasses(),
            text: 'Siguiente',
            action: async () => {
              await this.popoverController.dismiss();
              try {
                await this.popoverController.dismiss();
              } catch { }
              this.shepherdService.next();
            },
          },
        ],
        when: {
          destroy: () => {
            this.onboardingService.setOnboardingDashboard(false);
          },
        },
      },
      {
        beforeShowPromise: () => this.openApps(),
        attachTo: {
          element: 'app-yalo-apps',
          // on: 'auto',
        },
        modalOverlayOpeningPadding: 17,
        title: 'Recorrido',
        text: 'Accede rápidamente a Yalo Cobro POS.',
        buttons: [
          {
            classes: this.onboardingService.secondaryButtonClasses(),
            text: 'Cancelar',
            action: () => {
              this.shepherdService.cancel();
            },
          },
          {
            classes: this.onboardingService.primaryButtonClasses(),
            text: 'Siguiente',
            action: async () => {
              await this.popoverController.dismiss();
              try {
                await this.popoverController.dismiss();
              } catch { }
              this.shepherdService.next();
            },
          },
        ],
        when: {
          destroy: () => {
            this.onboardingService.setOnboardingDashboard(false);
          },
        },
      },
      {
        showOn: () => document.getElementById('yaloHelpButton') != null,
        attachTo: {
          element: '#yaloHelpButton',
          // on: 'auto',
        },
        title: 'Recorrido',
        text: '¿Tienes dudas? Accede a nuestra sección de ayuda.',
        buttons: [
          {
            classes: this.onboardingService.secondaryButtonClasses(),
            text: 'Cancelar',
            action: () => {
              this.shepherdService.cancel();
            },
          },
          {
            classes: this.onboardingService.primaryButtonClasses(),
            text: 'Siguiente',
            action: async () => {
              this.shepherdService.next();
            },
          },
        ],
        when: {
          destroy: () => {
            this.onboardingService.setOnboardingDashboard(false);
          },
        },
      },
      {
        attachTo: {
          element: '#sidebarNavigationContent',
          // on: 'auto',
        },
        title: 'Recorrido',
        text: '¿Tienes dudas? Accede a nuestra sección de ayuda.',
        buttons: [
          {
            classes: this.onboardingService.secondaryButtonClasses(),
            text: 'Cancelar',
            action: () => {
              this.shepherdService.cancel();
            },
          },
          {
            classes: this.onboardingService.primaryButtonClasses(),
            text: 'Finalizar',
            action: async () => {
              this.shepherdService.complete();
              this.onboardingService.setOnboardingDashboard(false);
            },
          },
        ],
        when: {
          destroy: () => {
            this.onboardingService.setOnboardingDashboard(false);
          },
        },
      },
    ];
  }

  openApps = async (): Promise<boolean> => {
    document.getElementById('yaloAppsButton').click();
    await this.later(300, true);
    return true;
  };

  later = (delayValue: number, value: boolean) =>
    new Promise((resolve) => setTimeout(resolve, delayValue, value));

  private obtenerEstadisticas() {
    this.destroyCharts();
    this.resetFilters();
    this._loading.isLoading = true;

    const selectedEstablecimientos = this.establecimientos?.filter(est => est.selected).map(est => est.CodEstablecimiento);
    const model = {
      FechaInicio_DT: this._dateFormat.converterFormatDateTime(this.fechaInicio),
      FechaFinal_DT: this._dateFormat.converterFormatDateTime(this.fechaFin),
      FechaInicio_ST: this._dateFormat.converterFormatDateTime(this.fechaInicio),
      FechaFinal_ST: this._dateFormat.converterFormatDateTime(this.fechaFin),
      CodEstablecimientos: selectedEstablecimientos,
    };
    console.log(model)
    this._dashboardService
      .getEstadisticas(model)
      .pipe(        
        map((data) => { 
          return data;
        }),
        finalize(() => (this._loading.isLoading = false))
      )
      .subscribe((data) => {
        this.dashboard = data;
        this.correlativos = data.Correlativos;
        this.tarjetas = data.Tarjetas;
        this.chartService.createChart(this.dashboard, this.filterType);
      });
  }

  onChangeDate(event: DaterangeEvent) {
    this.fechaInicio = event.detail.dateStart;
    this.fechaFin = event.detail.dateEnd;
    this.chartService.formattedInicio = this.chartService.formatDate(
      this.fechaInicio
    );
    this.chartService.formattedFin = this.chartService.formatDate(
      this.fechaFin
    );
    this.obtenerEstadisticas();
  }

  setFilterType(event) {
    const type = +event.target.value;
    if (type) {
      this.filterType = type;
      this.changeDatasets();
    }
  }

  setChartType(event) {
    const type = +event.target.value;
    if (type) {
      this.chartService.filterChart = type;
      this.chartService.changeChartFilter(this.dashboard, this.filterType);
    }
  }

  resetFilters() {
    this.chartService.filterChart = 1;
    this.filterType = 1;
  }

  changeDatasets() {
    //TODOmz: posiblidad de optimizar
    const data1 = this.chartService.chart1.data.datasets[0];
    const label1 = this.chartService.chart1.options.plugins.tooltip.callbacks;
    const data2 = this.chartService.chart2.data.datasets[0];
    const label2 = this.chartService.chart2.options.plugins.tooltip.callbacks;
    const data3 = this.chartService.chart3.data.datasets[0];
    const label3 = this.chartService.chart3.options.plugins.tooltip.callbacks;
    const data4 = this.chartService.chart4.data.datasets[0];
    const label4 = this.chartService.chart4.options.plugins.tooltip.callbacks;
    if (this.filterType === 1) {
      if (
        this.chartService.formattedInicio === this.chartService.formattedFin
      ) {
        data1.data = this.dashboard.VentasXHora.map(
          (ventas) => ventas.Total / ventas.Cantidad
        );
      } else {
        if (this.chartService.filterChart === 1) {
          data1.data = this.dashboard.Ventas.map((ventas) => ventas.Total);
        } else {
          data1.data = this.dashboard.VentasXHora.map(
            (ventas) => ventas.Total / ventas.Cantidad
          );
        }
      }
      data2.data = this.dashboard.TipoPedidos.map(
        (pedidos) => pedidos.Lempiras
      );
      data3.data = this.dashboard.FormasPago.map((formas) => formas.Valor);
      data4.data = this.dashboard.Categorias.map(
        (categorias) => categorias.Lps
      );
      label1.label = ({ formattedValue }) => `${this.configSvc.simboloMoneda} ${formattedValue}`;
      label3.label = ({ formattedValue }) => `${this.configSvc.simboloMoneda} ${formattedValue}`;
      label2.label = ({ formattedValue }) => `${this.configSvc.simboloMoneda} ${formattedValue}`;
      label4.label = ({ formattedValue }) => `${this.configSvc.simboloMoneda} ${formattedValue}`;
      this.updateCharts();
    } else {
      if (
        this.chartService.formattedInicio === this.chartService.formattedFin
      ) {
        data1.data = this.dashboard.VentasXHora.map(
          (ventas) => ventas.Cantidad
        );
      } else {
        if (this.chartService.filterChart === 1) {
          data1.data = this.dashboard.Ventas.map((ventas) => ventas.Cantidad);
        } else {
          data1.data = this.dashboard.VentasXHora.map(
            (ventas) => ventas.Cantidad
          );
        }
      }
      data2.data = this.dashboard.TipoPedidos.map(
        (pedidos) => pedidos.Cantidad
      );
      data3.data = this.dashboard.FormasPago.map((formas) => formas.Cantidad);
      data4.data = this.dashboard.Categorias.map(
        (categorias) => categorias.Cantidad
      );
      label1.label = ({ formattedValue }) => formattedValue;
      label2.label = ({ formattedValue }) => formattedValue;
      label3.label = ({ formattedValue }) => formattedValue;
      label4.label = ({ formattedValue }) => formattedValue;
      this.updateCharts();
    }
  }

  private updateCharts() {
    this.chartService.chart1.update();
    this.chartService.chart2.update();
    this.chartService.chart3.update();
    this.chartService.chart4.update();
  }

  private destroyCharts() {
    if (this.chartService.chart1) {
      this.chartService.chart1.destroy();
    }
    if (this.chartService.chart2) {
      this.chartService.chart2.destroy();
    }
    if (this.chartService.chart3) {
      this.chartService.chart3.destroy();
    }
    if (this.chartService.chart4) {
      this.chartService.chart4.destroy();
    }
  }

  AllEstablecimientosSelected(): boolean {
    return this.establecimientos?.length > 0 &&
           this.establecimientos.every(est => est.selected);
  }

  toggleAllEstablecimientos(): void {
    const shouldSelect = !this.AllEstablecimientosSelected();
    this.establecimientos.forEach(est => est.selected = shouldSelect);
    this.onEstablecimientoChange();
  }

  onEstablecimientoChange(): void {
    const selectedEstablecimientos = this.establecimientos
      .filter(est => est.selected)
      .map(est => est.CodEstablecimiento);

    if (selectedEstablecimientos.length > 0) {
      this.establecimientosService.codEstablecimiento = selectedEstablecimientos[0];
      this.obtenerEstadisticas();
    }
  }

  toggleEstablecimientos(event: Event) {
    event.stopPropagation();
    this.isEstablecimientosExpanded = !this.isEstablecimientosExpanded;
  }

  isCajaActiva(fecha: { seconds: number }) {
  if (!fecha) return false;
  const tiempoMs = fecha.seconds * 1000;
  const diferencia = Date.now() - tiempoMs;
  return diferencia < 1 * 60 * 1000; // 5 minutos
}


}
